import { useEffect, useRef } from "react";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { fetchAuthSession } from "aws-amplify/auth";
import { identifyUser, getOrganizationFromHostname } from "../lib/posthog";
import { useOrganizationStore } from "../store/useOrganizationStore";
import posthog from "posthog-js";

/**
 * Syncs PostHog distinct_id with the authenticated user.
 * Renders nothing; runs as a side effect when auth status changes.
 */
export default function PostHogIdentitySync() {
  const { authStatus } = useAuthenticator();
  const lastIdentifiedRef = useRef<string | null>(null);
  const organizations = useOrganizationStore((state) => state.organizations);
  const activeOrgId = useOrganizationStore((state) => state.activeOrgId);

  useEffect(() => {
    if (authStatus !== "authenticated") {
      lastIdentifiedRef.current = null;
      return;
    }

    let cancelled = false;

    const sync = async () => {
      try {
        const session = await fetchAuthSession();
        const userId = session.userSub ?? null;
        if (cancelled || !userId || userId === lastIdentifiedRef.current) return;

        identifyUser(userId, {
          email: session.tokens?.idToken?.payload?.email,
          organization: import.meta.env.VITE_ORGANIZATION || getOrganizationFromHostname(),
        });
        const activeOrg = organizations.find((org) => org.orgId === activeOrgId);
        if (activeOrg?.isDemo) {
          posthog.register({
            is_demo_user: true,
            demo_org_id: activeOrg.orgId,
          });
        }
        lastIdentifiedRef.current = userId;
      } catch {
        // Ignore auth errors
      }
    };

    sync();
    return () => {
      cancelled = true;
    };
  }, [authStatus, organizations, activeOrgId]);

  return null;
}
